import { List, Map, Set } from "immutable";
import { createSelector } from "reselect";
import { isBlankLocation, OpeningRecord, typedOpeningKey } from "common/records/Opening";
import { OpeningType } from "common/records/OpeningEstimation";
import { RootState } from "common/reducers";
import { BlockedLocationItem } from "common/helpers/Measurement";
import { currentMeasurement } from "common/selectors/measurement.common.selectors";
import { currentMeasurementWindows } from "common/selectors/window.selectors";
import { currentMeasurementDoors } from "common/selectors/door.selectors";
import { annotationLinkedOpeningKeys } from "common/selectors/elevation.selectors";

/**
 * Loads an opening (door or window) by its id. The lookup can miss — an unhydrated id, a deleted
 * record, or a stale reference all yield `undefined` — so callers must narrow before use.
 *
 * @param {RootState} state The RootState
 * @param {{ openingType: OpeningType, openingId: number }} props
 * @returns {WindowRecord | DoorRecord | undefined} the opening, or undefined when the id isn't hydrated
 */
export const openingById = (
  state: RootState,
  props: { openingId: number; openingType: OpeningType | "door" | "window" },
): OpeningRecord | undefined => {
  return state.getIn([`${props.openingType.toLowerCase()}s`, "byId", props.openingId]) as OpeningRecord | undefined;
};

/**
 * All live (non-destroyed) window and door openings of the current measurement, sorted the way the
 * Openings Wizard lists them (rsf-app `_setupOpenings`): UI-ascending, with name, then height, then
 * width as tiebreaks. Empty when no measurement is loaded.
 *
 * @param {RootState} state The RootState
 * @returns {List<OpeningRecord>} the sorted openings
 */
export const currentMeasurementOpenings = createSelector(
  [
    currentMeasurement,
    (state: RootState) =>
      (state.getIn(["windows", "byId"]) as Map<number, OpeningRecord>) ?? Map<number, OpeningRecord>(),
    (state: RootState) =>
      (state.getIn(["doors", "byId"]) as Map<number, OpeningRecord>) ?? Map<number, OpeningRecord>(),
  ],
  (measurement, windowsById, doorsById): List<OpeningRecord> => {
    if (!measurement) return List<OpeningRecord>();
    // Resolve ids from the byId input maps (equivalent to openingById's `<kind>s.byId` lookup) so the
    // selector memoizes on the windows/doors slices rather than re-running on every state change.
    const resolve = (ids: List<number>, byId: Map<number, OpeningRecord>) =>
      ids.map((id) => byId.get(id)).filter((o): o is OpeningRecord => Boolean(o) && o.get("_destroy") !== true);

    // Single-pass equivalent of chaining stable sorts width→height→name→ui: the last stable key
    // wins, so the effective priority is ui, then name, then height, then width. `compare` mirrors
    // Immutable's default comparator (numeric for ui/height/width, lexicographic for name).
    const compare = <T extends number | string>(a: T, b: T): number => (a > b ? 1 : a < b ? -1 : 0);

    return resolve(measurement.get("window_ids"), windowsById)
      .concat(resolve(measurement.get("door_ids"), doorsById))
      .sort(
        (a: OpeningRecord, b: OpeningRecord) =>
          compare(a.ui, b.ui) || compare(a.name, b.name) || compare(a.height, b.height) || compare(a.width, b.width),
      )
      .toList();
  },
);

/** A window or door that must supply a Location before the measurement can save. */
export interface BlockingOpening extends BlockedLocationItem {
  id: number;
}

const EMPTY_BLOCKING_OPENINGS = List<BlockingOpening>();

/**
 * Collects the blocking openings of one kind: every live, non-annotation opening missing a Location,
 * touched or not. Position is 1-based among the kind's live openings, the last resort for labelling a
 * row with neither name nor designator.
 */
const blockingOf = (
  kind: "window" | "door",
  openings: List<OpeningRecord | undefined>,
  linkedKeys: Set<string>,
): List<BlockingOpening> => {
  let blocking = List<BlockingOpening>();
  let position = 0;
  openings.forEach((opening) => {
    if (!opening || opening.get("_destroy") === true) return;
    position += 1;
    // Skip annotation-linked openings — the Window-inator enforces those. Membership is the
    // reload-stable `annotationLinkedOpeningKeys` derivation because the API never serializes the
    // opening's `annotation_id`. Position still counts them so labels stay aligned with the table.
    if (linkedKeys.has(typedOpeningKey(kind, opening.id))) return;
    if (!isBlankLocation(opening.location)) return;
    blocking = blocking.push({
      kind,
      id: opening.id,
      label: opening.name || opening.designator || String(position),
    });
  });
  return blocking;
};

/**
 * The current measurement's regular windows and doors that block a Measurements-tab save: any live,
 * non-`_destroy` opening missing a Location. Annotation-linked openings are excluded (the
 * Window-inator owns their Location), keyed by `annotationLinkedOpeningKeys`.
 *
 * @param state
 * @returns {List<BlockingOpening>} the blocking openings, empty when the save may proceed
 */
export const currentMeasurementBlockingOpenings = createSelector(
  [currentMeasurementWindows, currentMeasurementDoors, annotationLinkedOpeningKeys],
  (windows, doors, linkedKeys): List<BlockingOpening> => {
    const blocking = blockingOf("window", windows, linkedKeys).concat(blockingOf("door", doors, linkedKeys));
    return blocking.isEmpty() ? EMPTY_BLOCKING_OPENINGS : blocking;
  },
);

/**
 * Whether this opening is one of the ones blocking a save.
 *
 * @param state
 * @param {{ kind: "door" | "window", openingId: number }} props
 * @returns {boolean} true when the opening is missing a Location
 */
export const openingBlocksSave = (state: RootState, props: { kind: "door" | "window"; openingId: number }): boolean =>
  currentMeasurementBlockingOpenings(state).some(
    (blocking) => blocking.kind === props.kind && blocking.id === props.openingId,
  );

/**
 * The live (non-destroyed) opening ids of the given kind for the current measurement. Ids whose
 * opening record isn't hydrated are dropped. Empty when no measurement is loaded.
 *
 * @param {RootState} state The RootState
 * @param {{ kind: "door" | "window" }} props
 * @returns {List<number>} the live opening ids
 */
export const currentMeasurementFilteredOpeningIds = (state: RootState, props: { kind: "door" | "window" }) => {
  // Guard against a missing current measurement: `currentMeasurement` is undefined once the job/
  // measurement context unwinds (e.g. navigating the Openings table away to Customers clears
  // `currentJobId` → `currentMeasurementId` → no measurement) while this table is still mounted for
  // the transition render. Without this, `undefined.get(...)` throws.
  const measurement = currentMeasurement(state);
  if (!measurement) return List<number>();
  const openingIdKey = `${props.kind}_ids` as "window_ids" | "door_ids";
  return measurement.get(openingIdKey).filter((id) => {
    const opening = openingById(state, { openingId: id, openingType: props.kind });
    return opening != null && opening.get("_destroy") !== true;
  });
};
